import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ReservationCard from '../ReservationCard/ReservationCard';
import UnconfirmedReservationCard from '../UnconfirmedReservationsCard/UnconfirmedReservationCard';
import { supabaseFreeTable } from '../../utils/supabase/clientFreeTable';
import { supabase } from '../../utils/supabase/client';
import { useCancelReservationFreeTable } from '../../api/reservations';
import { useAddNotification } from '../../api/notifications';
import { useRestaurant } from '../../api/restaurant';

// Notification sounds
const notificationSound = new Audio('./notification.mp3');

const NotificationPopup = ({ isVisible, message, color }) => {
  if (!isVisible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        backgroundColor: color || '#4CAF50',
        color: 'white',
        padding: '15px 20px',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
        zIndex: 1000,
        animation: 'slideIn 0.3s ease-out',
      }}
    >
      <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{message}</div>
      <style>
        {`
          @keyframes slideIn {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
        `}
      </style>
    </div>
  );
};

const FreeTableReservations = ({
  reservationsFreeTable,
  unconfirmedReservations,
  tempPartySize,
  handleConfirm,
  handleDeny,
  setSelectedReservationId,
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [popupColor, setPopupColor] = useState('#4CAF50');
  const { mutate: cancelFreeTable } = useCancelReservationFreeTable();
  const { mutate: addNotification } = useAddNotification();
  const {data:restaurant} = useRestaurant();
  const queryClient = useQueryClient();
useEffect(() => {
  const playNotificationSound = () => {
    const notificationSound = new Audio('/sounds/notification.mp3');
    notificationSound.play().catch((err) => console.error('Playback failed:', err));
  };

  const playCancelSound = () => {
    const cancelSound = new Audio('/sounds/cancel.mp3');
    cancelSound.play().catch((err) => console.error('Playback failed:', err));
  };

  const reservationSubscription = supabaseFreeTable
    .channel('reservations')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'reservations',
      },
      (payload) => {
        const eventType = payload.eventType;
        const newStatus = payload.new?.status;
        const oldStatus = payload.old?.status;
        const reservationRestaurantId = payload.new?.restaurantid;

        if (reservationRestaurantId !== restaurant.id) {
          return;
        }

        const isInsert = eventType === 'INSERT';
        const isDecline =
          eventType === 'UPDATE' &&
          oldStatus !== 'CANCELED' &&
          newStatus === 'CANCELED';
          const isConfirmed =
            eventType === 'UPDATE' &&
            oldStatus === 'PENDING' &&
            newStatus === 'CONFIRMED';
          const isDenied =
            eventType === 'UPDATE' &&
            oldStatus === 'PENDING' &&
            newStatus === 'DECLINED';
            console.log("Declined:", isDenied); 
            console.log("Canceled: ", isDecline)
            console.log("New:", isInsert)
            console.log("Confirmed: ", isConfirmed)

           if (isInsert || isDecline || isConfirmed || isDenied) {
            if (isInsert) {
              setPopupMessage('Нова резервација ✔');
              setPopupColor('#4CAF50');
              playNotificationSound();
            } else if (isDecline) {
              setPopupMessage('Откажана резервација✘');
              cancelFreeTable({ freeTableId: payload.new.id });
              setPopupColor('#f44336');
              playCancelSound();
            } else if (isConfirmed) {
              setPopupMessage('Гостинот го потврди предлогот ✔');
              setPopupColor('#4CAF50');
              playNotificationSound();
            } else if (isDenied) {
              setPopupMessage('Гостинот го одби предлогот ✘');
              setPopupColor('#f44336');
              playCancelSound();
            }

          setShowPopup(true);
          queryClient.invalidateQueries({ queryKey: ['reservations', 'freeTable'] });
          queryClient.invalidateQueries({ queryKey: ['reservations'] });


          setTimeout(() => setShowPopup(false), 5000);
        }
      }
    )
    .subscribe((status) => {
    });

  const unconfirmedSubscription = supabaseFreeTable
    .channel('unconfirmedReservations')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'unconfirmedReservations',
      },
      (payload) => {
        const reservationRestaurantId = payload.new?.restaurantId; // Adjust if column is restaurant_id
        if (reservationRestaurantId !== restaurant.id) {
          return;
        }

        setPopupMessage('Нова резервација ✔');
        setPopupColor('#4CAF50');
        setShowPopup(true);
        playNotificationSound();

        queryClient.invalidateQueries({ queryKey: ['reservations', 'unconfirmedFreeTable'] });

        setTimeout(() => setShowPopup(false), 5000);
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'unconfirmedReservations',
      },
      (payload) => {
        const oldStatus = payload.old?.status;
        const newStatus = payload.new?.status;
        const reservationRestaurantId = payload.new?.restaurantId; // Adjust if column is restaurant_id

        if (reservationRestaurantId !== restaurant.id) {
          return;
        }

        if (!newStatus) {
          console.warn('Missing newStatus in payload:', payload);
          return;
        }

        const isDecline = newStatus === 'CANCELED' && (!oldStatus || oldStatus !== 'CANCELED');

        if (isDecline) {
          setPopupMessage('Откажана резервација✘');
          cancelFreeTable({ freeTableId: payload.new.id });
          setPopupColor('#f44336');
          playCancelSound();

          setShowPopup(true);
          queryClient.invalidateQueries({ queryKey: ['reservations', 'unconfirmedFreeTable'] });

          setTimeout(() => setShowPopup(false), 5000);
        }
      }
    )
    .subscribe((status) => {
    });

  const notificationSubscription = supabase
    .channel('notifications-channel')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
      },
      (payload) => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['reservations'] });

      }
    )
    .subscribe((status) => {
    });

  return () => {
    supabaseFreeTable.removeChannel(reservationSubscription);
    supabaseFreeTable.removeChannel(unconfirmedSubscription);
    supabase.removeChannel(notificationSubscription);
  };
}, [queryClient, cancelFreeTable, restaurant.id]);

  // Combine reservations with type indicators
  const combinedReservations = [
    ...reservationsFreeTable.map((res) => ({ ...res, type: 'freeTable' })),
    ...unconfirmedReservations.map((res) => ({ ...res, type: 'unconfirmed' })),
  ];

  return (
    <div
      style={{
        height: '50%',
        width: '100%',
        backgroundColor: 'white',
        margin: '10px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Notification Popup */}
      <NotificationPopup isVisible={showPopup} message={popupMessage} color={popupColor} />

      {/* Header */}
      <div
        style={{
          padding: '10px',
          borderBottom: '1px solid #3fb488',
        }}
      >
        <div
          style={{
            fontWeight: 'bold',
            fontSize: '16px',
            marginBottom: '12px',
          }}
        >
          Непотврдени резервации
        </div>
      </div>

      {/* Combined Reservations List */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '10px',
        }}
      >
        {combinedReservations.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            Нема непотврдени резервации
          </div>
        ) : (
          combinedReservations.map((reservation) =>
            reservation.type === 'freeTable' ? (
              <div key={reservation.id} style={{ position: 'relative', marginBottom: '10px' }}>
                <span
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10%',
                    backgroundColor: '#e0f7fa',
                    color: '#0288d1',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                >
                  Апликација
                </span>
                <ReservationCard
                  reservation={reservation}
                  tempPartySize={tempPartySize}
                  handleConfirm={handleConfirm}
                  handleDeny={handleDeny}
                  setSelectedReservationId={setSelectedReservationId}
                />
              </div>
            ) : (
              <div key={reservation.id} style={{ position: 'relative', marginBottom: '10px' }}>
                <span
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10%',
                    backgroundColor: '#ffebee',
                    color: '#c62828',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                >
                  Веб
                </span>
                <UnconfirmedReservationCard
                  reservation={reservation}
                  tempPartySize={tempPartySize}
                  handleConfirm={handleConfirm}
                  handleDeny={handleDeny}
                />
              </div>
            )
          )
        )}
      </div>
    </div>
  );
};

export default FreeTableReservations;